import React, { useMemo, useState } from "react";
import { Lightbulb, ScrollText } from "lucide-react";
import { ALL_SYMPTOMS, shuffle } from "../../data/sporeGames";

export const RiddleGame = ({ entry, onWin, onPenalty }) => {
  const [wrong, setWrong] = useState([]);
  const [correct, setCorrect] = useState(false);
  const [hint, setHint] = useState(false);

  const options = useMemo(() => {
    const others = ALL_SYMPTOMS.filter((s) => s.symptom !== entry.symptom);
    const same = shuffle(others.filter((s) => s.category === entry.category));
    const rest = shuffle(others.filter((s) => s.category !== entry.category));
    const decoys = [...same.slice(0, 2), ...rest].slice(0, 3).map((s) => s.symptom);
    return shuffle([entry.symptom, ...decoys]);
  }, [entry]);

  const pick = (opt) => {
    if (correct || wrong.includes(opt)) return;
    if (opt === entry.symptom) {
      setCorrect(true);
      onWin();
    } else {
      setWrong((w) => [...w, opt]);
      onPenalty(10);
    }
  };

  const showHint = () => {
    if (hint) return;
    setHint(true);
    onPenalty(5);
  };

  return (
    <div className="space-y-6" data-testid="riddle-game">
      <figure className="relative rounded-sm border border-purple-500/40 bg-purple-950/20 px-6 py-6 sm:px-8 spore-fade-up">
        <ScrollText className="absolute top-4 right-4 h-5 w-5 text-purple-400/60" />
        <p className="font-tac text-xs tracking-[0.3em] text-purple-300 mb-4">THE SCIENTIST'S RIDDLE</p>
        <blockquote data-testid="riddle-text" className="space-y-1.5">
          {entry.riddle.map((line, i) => (
            <p key={line} className={`font-display text-lg sm:text-xl leading-snug ${i === entry.riddle.length - 1 ? "text-purple-200 font-bold mt-3" : "text-slate-100 italic"}`}>
              {line}
            </p>
          ))}
        </blockquote>
      </figure>
      {hint ? (
        <p data-testid="riddle-hint" className="spore-fade-up border-l-2 border-amber-500/70 bg-amber-950/20 px-4 py-3 text-slate-200">
          <span className="font-tac text-xs text-amber-300 mr-3">HINT</span>
          {entry.clues[1]}
        </p>
      ) : (
        <button type="button" data-testid="riddle-hint-btn" onClick={showHint} className="font-tac text-xs text-amber-300 hover:text-amber-200 underline underline-offset-4 flex items-center gap-2">
          <Lightbulb className="h-3.5 w-3.5" /> Ask for a hint (−5s)
        </button>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {options.map((opt, i) => {
          const isWrong = wrong.includes(opt);
          const isRight = correct && opt === entry.symptom;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`riddle-option-${i}`}
              onClick={() => pick(opt)}
              disabled={isWrong || correct}
              className={`text-left px-4 py-4 rounded-sm border font-display uppercase tracking-wide transition-[background-color,border-color,transform] duration-200 active:scale-[0.98] ${
                isRight
                  ? "border-emerald-400 bg-emerald-950/60 text-emerald-200"
                  : isWrong
                  ? "border-red-900 bg-red-950/40 text-red-400/60 line-through spore-shake"
                  : "border-slate-700 bg-slate-900/80 text-slate-100 hover:border-purple-400 hover:bg-purple-950/40"
              }`}
            >
              <span className="font-tac text-xs text-slate-500 mr-3">{String.fromCharCode(65 + i)}</span>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};
